








































import { Component, Vue } from 'vue-property-decorator';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import ActivitiesList from '@/componentsV2/organisms/ActivitiesList/ActivitiesList.vue';
import Trigger from '@/componentsV2/molecules/Trigger/Trigger.vue';

import { IPeriod } from '@/models/period';
import {
  EActivityStatus,
  ICustomerActivity,
  IInitiativeFilter,
  ICustomerActivityTableRow
} from '@/models/performance';

import PerformanceActivitiesService from '@/api/PerformanceActivitiesService';

import PerformanceUtils from '@/utils/PerformanceUtils';
import PaginationUtils, {
  EPaginationSortDir,
  IPagination,
  IPaginationTableHeader
} from '@/utils/PaginationUtils';
import { applyPerformanceDataFilter } from '@/views/Performance/flag-helpers';
import flagKeys from '@/constants/flagKeys';
import DateUtils from '@/utils/DateUtils';

@Component({
  components: {
    SectionData,
    ActivitiesList,
    Trigger
  }
})
export default class PerformanceActivitiesList extends Vue {
  performanceActivitiesService: PerformanceActivitiesService = new PerformanceActivitiesService();
  period: IPeriod = this.initialPeriod;
  initiatives: IInitiativeFilter[] = this.prevRouteIsCustomerDetail
    ? this.cachedInitiatives
    : [];
  activities: ICustomerActivityTableRow[] = this.prevRouteIsCustomerDetail
    ? this.cachedList
    : [];
  headers: Record<string, IPaginationTableHeader> = {
    createdAt: {
      label: 'Date',
      sortable: true,
      sortDir: EPaginationSortDir.DESC
    },
    customer: {
      label: 'Member'
    },
    action: {
      label: 'Action'
    },
    initiative: {
      label: 'Initiative',
      sortable: true
    }
  };
  pagination: IPagination = this.prevRouteIsCustomerDetail
    ? this.cachedPagination
    : PaginationUtils.defaultPagination();
  filters: Record<string, any> = this.prevRouteIsCustomerDetail
    ? this.cachedFilters
    : Object.assign({}, this.initialPeriod);
  search = {
    placeholder: 'Search by name',
    term:
      this.prevRouteIsCustomerDetail && this.cachedFilters.search
        ? this.cachedFilters.search
        : ''
  };

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get tPrefix() {
    return `performance.activities.list`;
  }

  get filterGATracking() {
    return {
      eventCategory: 'Customer Activities List - Tag Filter',
      eventAction: 'click'
    };
  }

  get gaTracking() {
    return {
      search: {
        eventCategory: 'Customer Activities List - Search',
        eventAction: 'change'
      },
      period: {
        eventCategory: 'Customer Activities List - Period',
        eventAction: 'change'
      }
    };
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get businessReferralIsActivated() {
    return this.flags.includes(flagKeys.businessReferral);
  }

  get customerImportIsActivated() {
    return this.flags.includes(flagKeys.customerImport);
  }

  get cachedList() {
    return this.$store.getters['performance/getActivityState'].list;
  }

  get cachedInitiatives() {
    return this.$store.getters['performance/getActivityState'].initiatives;
  }

  get initialPeriod() {
    const { from = null, to = null } = this.cachedFilters;

    if (!this.prevRouteIsCustomerDetail || !from || !to) {
      return PerformanceUtils.defaultPeriod();
    }

    return {
      start: from,
      end: to
    };
  }

  get cachedPagination() {
    return this.$store.getters['performance/getActivityState'].config
      .pagination;
  }

  get prevRouteIsCustomerDetail() {
    return (
      window.previousRouteName &&
      window.previousRouteName === 'performance-customer-center-detail'
    );
  }

  get cachedFilters() {
    return this.$store.getters['performance/getActivityState'].config.filters;
  }

  handleItemClick(rowData: Record<string, any>) {
    const containerScrollElement = document.querySelector(
      '.container-scrollable'
    );

    this.$store.dispatch('performance/SET_CONFIG', {
      context: 'activity',
      config: {
        pagination: this.pagination,
        filters: this.filters,
        ...(containerScrollElement && {
          scrollYPos: containerScrollElement.scrollTop
        })
      }
    });
    this.$store.dispatch('performance/SET_INITIATIVES', {
      context: 'activity',
      initiatives: this.initiatives
    });
    this.$store.dispatch('performance/SET_LIST_DATA', {
      context: 'activity',
      list: this.activities
    });
  }

  isCanceledActivity(status: EActivityStatus) {
    return status === EActivityStatus.CANCELED;
  }

  buildActionMessage(message: string, status: EActivityStatus) {
    return this.isCanceledActivity(status)
      ? `<s>${message}</s><span class="action-status"> (CANCELED)</span>`
      : message;
  }

  formatedActivities(
    activities: ICustomerActivity[]
  ): ICustomerActivityTableRow[] {
    const formatActivity = ({
      activity: { createdAt, initiative, message, label, status },
      customer
    }: ICustomerActivity) => {
      const date = DateUtils.formattedDate({
        date: createdAt,
        applyTimezone: true
      });
      const hour = DateUtils.formattedDate({
        date: createdAt,
        format: 'h:mm a',
        applyTimezone: true
      });

      return {
        date: this.isMobileDevice
          ? `${date} • ${hour}`
          : `${date}<br />${hour}`,
        initiativeSlug: initiative,
        initiativeLabel: label,
        customer,
        action: this.buildActionMessage(message, status),
        status
      };
    };

    return activities.map(formatActivity);
  }

  async loadActivities({ clearList = false, showLoader = true } = {}) {
    if (clearList) {
      this.pagination = PaginationUtils.defaultPagination();
    }

    if (!this.pagination.mustCallApi) return;

    let endpointPath = '/v2/admin/activity/business';
    let queryParameters = this.filters;

    if (this.pagination.next) {
      endpointPath = this.pagination.next;
      queryParameters = clearList ? this.filters : {};
    }

    if (showLoader) this.$showLoaderWithLocker();

    const response = await this.performanceActivitiesService.getActivitiesList(
      endpointPath,
      queryParameters
    );

    if (showLoader) this.$hideLoader();

    if (response.status !== 200) return;

    const { items, links, filters } = response.data;

    if (filters) {
      this.initiatives = filters.initiatives
        .filter(
          applyPerformanceDataFilter(
            this.businessReferralIsActivated,
            'type',
            flagKeys.referralProgram
          )
        )
        .filter(
          applyPerformanceDataFilter(
            this.customerImportIsActivated,
            'type',
            flagKeys.customerImport
          )
        );
    }

    Object.assign(this.pagination, {
      next: links.next,
      mustCallApi: !!links.next
    });

    if (clearList) {
      this.activities = this.formatedActivities(items);

      return;
    }

    this.activities = [...this.activities, ...this.formatedActivities(items)];

    PaginationUtils.makeNewRequest(
      {
        triggerClass: '.infinite-trigger',
        hasPagination: !!this.pagination.next
      },
      () => {
        this.loadActivities({ showLoader: false });
      }
    );
  }

  handleSort(field: string) {
    this.headers = PaginationUtils.getUpdatedHeaders(field, this.headers);

    const sortDir = this.headers[field].sortDir;

    this.filters = {
      ...this.filters,
      sort: `activity.${field}:${sortDir}`
    };

    this.$analytics({
      eventAction: 'click',
      eventCategory: 'Customer Activities List - Sort',
      eventLabel: `[${sortDir}] ${this.headers[field].label} column`
    });

    this.loadActivities({ clearList: true });
  }

  handleFilterChange(initiatives: string[]) {
    this.filters = {
      ...this.filters,
      initiatives
    };

    this.loadActivities({ clearList: true });
  }

  handlePeriodChange(period: IPeriod) {
    const newPeriod = PerformanceUtils.normalizedPeriod(period);

    this.period = newPeriod;
    this.filters = {
      ...this.filters,
      from: newPeriod.start,
      to: newPeriod.end
    };

    this.loadActivities({ clearList: true });
  }

  handleSearchFilterChange(search: any) {
    delete this.filters.search;

    if (this.search.term) {
      this.filters = {
        ...this.filters,
        search: this.search.term
      };
    }

    this.loadActivities({ clearList: true });
  }
}
